import type { GoalStatus, PlayerGoal } from "@/lib/types";
import { GoalCard } from "./GoalCard";

/**
 * The per-player goal list (M7) — rendered inside the
 * `goalsSlot` of `<PlayerDevelopmentProfile>`. Ports the legacy
 * `_renderDevGoals` goal grid (`js/coaching/development.js`).
 *
 * Presentational — it maps `goals` to `<GoalCard>`s and surfaces
 * the coach-only "+ New goal" button. All actions
 * (status / edit / delete / reflection / new) are delegated to
 * the host via callbacks; the host owns the modal state.
 *
 * `viewer` flows straight through to each card — the privacy
 * gating (`coach_private_note`, coach meta, action row) lives in
 * `GoalCard`.
 */

export type GoalListProps = {
  goals: PlayerGoal[];
  viewer: boolean;
  // --- coach ---
  onNewGoal?: () => void;
  onChangeStatus?: (goalId: number, status: GoalStatus) => void;
  onEdit?: (goal: PlayerGoal) => void;
  onDelete?: (goal: PlayerGoal) => void;
  // --- viewer ---
  onAddReflection?: (goal: PlayerGoal) => void;
};

export function GoalList({
  goals,
  viewer,
  onNewGoal,
  onChangeStatus,
  onEdit,
  onDelete,
  onAddReflection,
}: GoalListProps) {
  return (
    <div className="flex flex-col gap-2" data-testid="goal-list">
      {/* Coach-only "+ New goal" — opens the M6a GoalComposerModal. */}
      {!viewer && onNewGoal ? (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onNewGoal}
            data-testid="goal-list-new"
            className="bg-accent hover:bg-accent-hover text-white rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors"
          >
            + New goal
          </button>
        </div>
      ) : null}

      {goals.length === 0 ? (
        <p
          className="text-ink-muted text-sm"
          data-testid="goal-list-empty"
        >
          {viewer
            ? "No goals shared with you yet."
            : "No active goals yet. Create one to give the player an explicit action plan."}
        </p>
      ) : (
        goals.map((goal) => (
          <GoalCard
            key={goal.id}
            goal={goal}
            viewer={viewer}
            onChangeStatus={onChangeStatus}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddReflection={onAddReflection}
          />
        ))
      )}
    </div>
  );
}
